/**
 * DealIntelCard — Intelligence briefing displayed after a deal sweep.
 *
 * Shows property snapshot, key metrics, threats/opportunities,
 * 7-step underwriting progress stepper, and gap action chips.
 */

const GAP_QUESTIONS: Record<string, string> = {
  "Property basic data incomplete": "Tell me more about this property",
  "Tax analysis not completed": "What would the taxes be if I bought this?",
  "Site screening not completed": "Screen this site for zoning, flood, and environmental issues",
  "Market analysis not completed": "What's the market like in this area?",
  "Owner research not completed": "Who owns this property and what else do they own?",
  "Financial structuring data missing": "Run a DSCR and LTV analysis",
};

export { GAP_QUESTIONS };

interface DealIntelCardProps {
  data: {
    address: string | null;
    parcel_id: string | null;
    property_type: string | null;
    assessed_value: number | null;
    building_sf: number | null;
    acreage: number | null;
    current_owner: string | null;
    completeness_score: number;
    risk_score: number | null;
    investor_tax_annual: number | null;
    tax_delta: number | null;
    median_price: number | null;
    median_price_per_sf: number | null;
    threats: string[];
    opportunities: string[];
    gaps: string[];
    pir_status: Record<string, string>;
  };
  onFollowUp?: (message: string) => void;
}

const STEPS: Array<{ label: string; pirKey: string | null }> = [
  { label: "Property Basics", pirKey: "property_basics" },
  { label: "Market & Comps", pirKey: "market_analysis" },
  { label: "Tax Analysis", pirKey: "tax_analysis" },
  { label: "Site Screening", pirKey: "site_screening" },
  { label: "Owner Research", pirKey: "owner_research" },
  { label: "Financial Structure", pirKey: "financial_structuring" },
  { label: "Final Report", pirKey: null },
];

function formatCurrency(n: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(n);
}

function riskColor(score: number): string {
  if (score < 20) return "var(--success, #10b981)";
  if (score <= 60) return "var(--warning, #eab308)";
  return "var(--error, #ef4444)";
}

function completenessColor(score: number): string {
  if (score >= 75) return "var(--success, #10b981)";
  if (score >= 50) return "var(--warning, #eab308)";
  return "var(--accent, #3b82f6)";
}

function stepStatus(
  pirKey: string | null,
  pirStatus: Record<string, string>,
  completeness: number,
): "complete" | "partial" | "unknown" {
  if (pirKey === null) return completeness >= 80 ? "complete" : "unknown";
  const val = pirStatus[pirKey];
  if (val === "complete") return "complete";
  if (val === "partial") return "partial";
  return "unknown";
}

export default function DealIntelCard({ data, onFollowUp }: DealIntelCardProps) {
  const {
    address,
    property_type,
    current_owner,
    completeness_score,
    risk_score,
    investor_tax_annual,
    tax_delta,
    median_price_per_sf,
    threats,
    opportunities,
    gaps,
    pir_status,
  } = data;

  // Completeness ring
  const ringSize = 32;
  const sw = 3;
  const r = (ringSize - sw) / 2;
  const circ = 2 * Math.PI * r;
  const filled = (completeness_score / 100) * circ;
  const ringClr = completenessColor(completeness_score);

  return (
    <div className="intel-card">
      {/* Header */}
      <div className="intel-header">
        <div className="intel-header-left">
          {property_type && <span className="intel-badge">{property_type}</span>}
          <span className="intel-address">{address || "Unknown address"}</span>
        </div>
        {current_owner && (
          <span className="intel-owner">{current_owner}</span>
        )}
      </div>

      {/* Metrics Row */}
      <div className="intel-metrics">
        {tax_delta != null && (
          <MetricCell
            label="Tax Impact"
            value={`${tax_delta > 0 ? "+" : ""}${formatCurrency(tax_delta)}/yr`}
            valueColor={tax_delta > 0 ? "var(--error, #ef4444)" : "var(--success, #10b981)"}
            sub={investor_tax_annual != null ? `${formatCurrency(investor_tax_annual)} total` : undefined}
          />
        )}
        {risk_score != null && (
          <MetricCell
            label="Risk Score"
            value={`${risk_score}/100`}
            valueColor={riskColor(risk_score)}
          />
        )}
        {median_price_per_sf != null && (
          <MetricCell
            label="Market"
            value={`$${median_price_per_sf}/sf`}
            valueColor="var(--text-primary, #f1f5f9)"
          />
        )}
        <div className="intel-metric-cell">
          <div className="intel-metric-label">Completeness</div>
          <div className="intel-metric-ring">
            <svg width={ringSize} height={ringSize} viewBox={`0 0 ${ringSize} ${ringSize}`}>
              <circle cx={ringSize / 2} cy={ringSize / 2} r={r} fill="none" stroke="var(--border, #1e293b)" strokeWidth={sw} />
              <circle
                cx={ringSize / 2} cy={ringSize / 2} r={r} fill="none"
                stroke={ringClr} strokeWidth={sw}
                strokeDasharray={`${filled} ${circ}`}
                strokeLinecap="round"
                transform={`rotate(-90 ${ringSize / 2} ${ringSize / 2})`}
                style={{ transition: "stroke-dasharray 0.5s ease" }}
              />
            </svg>
            <span className="intel-metric-pct">{completeness_score}%</span>
          </div>
        </div>
      </div>

      {/* Threats */}
      {threats.length > 0 && (
        <div className="intel-section intel-threats">
          {threats.map((t, i) => (
            <div key={i} className="intel-row threat-row">
              <span className="intel-icon">&#9888;</span>
              <span>{t}</span>
            </div>
          ))}
        </div>
      )}

      {/* Opportunities */}
      {opportunities.length > 0 && (
        <div className="intel-section intel-opps">
          {opportunities.map((o, i) => (
            <div key={i} className="intel-row opp-row">
              <span className="intel-icon">&#8599;</span>
              <span>{o}</span>
            </div>
          ))}
        </div>
      )}

      {/* Underwriting Stepper */}
      <div className="intel-stepper">
        {STEPS.map((step, i) => {
          const s = stepStatus(step.pirKey, pir_status, completeness_score);
          return (
            <div key={i} className="intel-step">
              <div className={`intel-step-circle intel-step--${s}`}>
                {s === "complete" ? "\u2713" : i + 1}
              </div>
              <div className="intel-step-label">{step.label}</div>
            </div>
          );
        })}
      </div>

      {/* Gap Actions */}
      {gaps.length > 0 && onFollowUp && (
        <div className="intel-gaps">
          <span className="intel-gaps-label">Close the gaps:</span>
          <div className="intel-chips">
            {gaps.map((gap) => {
              const question = GAP_QUESTIONS[gap];
              if (!question) return null;
              return (
                <button
                  key={gap}
                  className="intel-chip"
                  onClick={() => onFollowUp(question)}
                >
                  {gap.replace(/ not completed| data missing| data incomplete/g, "")}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <style>{intelStyles}</style>
    </div>
  );
}

function MetricCell({
  label,
  value,
  valueColor,
  sub,
}: {
  label: string;
  value: string;
  valueColor: string;
  sub?: string;
}) {
  return (
    <div className="intel-metric-cell">
      <div className="intel-metric-label">{label}</div>
      <div className="intel-metric-value" style={{ color: valueColor }}>
        {value}
      </div>
      {sub && <div className="intel-metric-sub">{sub}</div>}
    </div>
  );
}

const intelStyles = `
  .intel-card {
    background: var(--surface, #111827);
    border: 1px solid var(--border, #1e293b);
    border-radius: 12px;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  .intel-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
  }
  .intel-header-left {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .intel-badge {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    padding: 2px 8px;
    border-radius: 4px;
    background: var(--accent-glow, rgba(59,130,246,0.15));
    color: var(--accent, #3b82f6);
  }
  .intel-address {
    font-size: 14px;
    font-weight: 600;
    color: var(--text-primary, #f1f5f9);
  }
  .intel-owner {
    font-size: 12px;
    color: var(--text-dim, #64748b);
  }
  .intel-metrics {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
    gap: 8px;
  }
  .intel-metric-cell {
    background: var(--bg, #0c0f14);
    border-radius: 8px;
    padding: 10px 12px;
  }
  .intel-metric-label {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--text-dim, #64748b);
    margin-bottom: 4px;
  }
  .intel-metric-value {
    font-size: 17px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
  }
  .intel-metric-sub {
    font-size: 11px;
    color: var(--text-dim, #64748b);
  }
  .intel-metric-ring {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .intel-metric-pct {
    font-size: 17px;
    font-weight: 700;
    color: var(--text-primary, #f1f5f9);
    font-variant-numeric: tabular-nums;
  }
  .intel-section {
    border-radius: 8px;
    padding: 8px 12px;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .intel-threats {
    background: rgba(239, 68, 68, 0.06);
    border-left: 3px solid var(--error, #ef4444);
  }
  .intel-opps {
    background: rgba(16, 185, 129, 0.06);
    border-left: 3px solid var(--success, #10b981);
  }
  .intel-row {
    display: flex;
    align-items: flex-start;
    gap: 6px;
    font-size: 13px;
    color: var(--text-muted, #94a3b8);
  }
  .threat-row .intel-icon { color: var(--error, #ef4444); }
  .opp-row .intel-icon { color: var(--success, #10b981); }
  .intel-icon { flex-shrink: 0; }
  .intel-stepper {
    display: flex;
    justify-content: space-between;
    gap: 4px;
    padding: 8px 0;
    overflow-x: auto;
  }
  .intel-step {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    min-width: 0;
    flex: 1;
  }
  .intel-step-circle {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: 700;
    flex-shrink: 0;
  }
  .intel-step--complete {
    background: var(--success, #10b981);
    color: #fff;
  }
  .intel-step--partial {
    background: transparent;
    border: 2px solid var(--warning, #eab308);
    color: var(--warning, #eab308);
  }
  .intel-step--unknown {
    background: transparent;
    border: 2px solid var(--border, #1e293b);
    color: var(--text-dim, #64748b);
  }
  .intel-step-label {
    font-size: 10px;
    color: var(--text-dim, #64748b);
    text-align: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 80px;
  }
  .intel-gaps {
    padding-top: 4px;
  }
  .intel-gaps-label {
    font-size: 12px;
    font-weight: 600;
    color: var(--text-muted, #94a3b8);
    margin-bottom: 6px;
    display: block;
  }
  .intel-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }
  .intel-chip {
    font-size: 12px;
    padding: 4px 10px;
    border-radius: 16px;
    border: 1px solid var(--border, #1e293b);
    background: var(--bg, #0c0f14);
    color: var(--accent, #3b82f6);
    cursor: pointer;
    transition: background 0.15s, border-color 0.15s;
  }
  .intel-chip:hover {
    background: var(--accent-glow, rgba(59,130,246,0.15));
    border-color: var(--accent, #3b82f6);
  }
  @media (max-width: 640px) {
    .intel-metrics {
      grid-template-columns: repeat(2, 1fr);
    }
    .intel-stepper {
      gap: 2px;
    }
    .intel-step-label {
      font-size: 9px;
      max-width: 50px;
    }
  }
`;
